// Lib
import React from 'react';
import { useSelector } from 'react-redux';

// Utils
import { getAreDatesSameDay, getDateIsLessThan } from '../../../common/utils/timeUtil';
import { getPlatformTypeSelector } from '../../platform/platformSelector';

// Styles
import './TimeInput.scss';

interface TimeInputProps {
    value: Date;
    onChange: (newDate: Date) => void;
    min?: Date;
}

const range = (start: number, end: number, step = 1): number[] =>
    Array.from({ length: Math.ceil((end - start) / step) }, (_, i) => start + i * step);

const hoursOptions = range(1, 12);
hoursOptions.unshift(12);

const minutesOptions = range(0, 60, 5);
const meridiemOptions = ['am', 'pm'];

const getMinimumMinutes = (value: Date, min: Date) => {
    // If the selected date is less than the minimum date, then no minutes can be selected
    if (getDateIsLessThan(value, min) && !getAreDatesSameDay(value, min)) return 60;

    // If the selected date is greater than the minimum date, then every minute can be selected
    if (!getAreDatesSameDay(value, min)) return 0;

    const valueHours24 = value.getHours();
    const minHours24 = min.getHours();

    // The selected hour is larger than the minimum, so every minute can be selected
    if (valueHours24 > minHours24) return 0;

    // The selected hour is less than the minimum, so no minutes can be selected
    if (valueHours24 < minHours24) return 60;

    // The selected hour is the same as the minimum, so only the minimum minutes and above can be selected
    return min.getMinutes();
};

const getMeridiemMinimumIndex = (value: Date, min: Date) => {
    // If the selected date is less than the minimum date, then neither of the meridiems can be selected
    if (getDateIsLessThan(value, min) && !getAreDatesSameDay(value, min)) return 2;

    // If the selected date is greater than the minimum date, then both meridiems can be selected
    if (!getAreDatesSameDay(value, min)) return 0;

    // const valueHours24 = value.getHours();
    // const isValuePm = valueHours24 > 11;

    const minHours24 = min.getHours();
    const isMinPm = minHours24 > 11;

    // If the minimum is PM only the PM option can be selected
    return isMinPm ? 1 : 0;
};

const dateToTimeString = (date: Date): string => {
    if (!date) return '';
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
};

const timeStringToDate = (date: Date, timeStr: string): Date => {
    if (!date || !timeStr) return date;
    const [hoursStr, minutesStr] = timeStr.split(':');
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);
    const newDate = new Date(date);
    newDate.setHours(hours, minutes, 0, 0);
    return newDate;
};

const TimeInput: React.FC<TimeInputProps> = ({ value, onChange, min }) => {
    const date = value || new Date();
    const hours24 = date.getHours();
    const isValuePm = hours24 > 11;
    const hours12 = hours24 > 12 ? hours24 - 12 : hours24 || 12;
    const minutesRoundedTo5 = Math.floor(date.getMinutes() / 5) * 5;

    const minimumMinutes = min ? getMinimumMinutes(date, min) : 0;
    const minimumMeridiemIndex = min ? getMeridiemMinimumIndex(date, min) : 0;

    const isAndroid = useSelector(getPlatformTypeSelector) === 'android';

    const onHourChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const updatedDate = new Date(date);

        let newHours = parseInt(event.target.value, 10);

        if (isValuePm && newHours !== 12) {
            newHours += 12;
        } else if (!isValuePm && newHours === 12) {
            newHours = 0;
        }

        updatedDate.setHours(newHours);

        onChange(updatedDate);
    };

    const onMinuteChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const updatedDate = new Date(date);
        updatedDate.setMinutes(parseInt(event.target.value, 10));
        onChange(updatedDate);
    };

    const onMeridiemChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const updatedDate = new Date(date);

        const isPm = event.target.value === 'pm';
        let newHours = hours12;

        if (isPm && newHours !== 12) {
            newHours += 12;
        } else if (!isPm && newHours === 12) {
            newHours = 0;
        }

        updatedDate.setHours(newHours);

        onChange(updatedDate);
    };

    const handleNativeTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newTimeStr = event.target.value;
        const updatedDate = timeStringToDate(value, newTimeStr);
        onChange(updatedDate);
    };

    return (
        <div className="TimeInput">
            <div className="input">
                {isAndroid ? (
                    <input
                        type="time"
                        value={dateToTimeString(value)}
                        onChange={handleNativeTimeChange}
                        className={`android-time-input`}
                    />
                ) : (
                    <>
                        <select className="hours" value={hours12} onChange={onHourChange}>
                            {hoursOptions.map((hourOption: number) => (
                                <option key={hourOption} value={hourOption}>
                                    {String(hourOption).padStart(2, '0')}
                                </option>
                            ))}
                        </select>
                        <span className="separator">:</span>
                        <select className="minutes" value={minutesRoundedTo5} onChange={onMinuteChange}>
                            {minutesOptions.map((minuteOption: number) => (
                                <option
                                    key={minuteOption}
                                    value={minuteOption}
                                    disabled={minuteOption < minimumMinutes}
                                >
                                    {String(minuteOption).padStart(2, '0')}
                                </option>
                            ))}
                        </select>
                        <select className="meridiem" value={isValuePm ? 'pm' : 'am'} onChange={onMeridiemChange}>
                            {meridiemOptions.map((meridiemOption, index) => (
                                <option
                                    key={meridiemOption}
                                    value={meridiemOption}
                                    disabled={index < minimumMeridiemIndex}
                                >
                                    {meridiemOption}
                                </option>
                            ))}
                        </select>
                    </>
                )}
            </div>
        </div>
    );
};

export default TimeInput;
